import { Button, ButtonGroup, Classes, Collapse, ControlGroup, FormGroup, MenuItem, Spinner, Tooltip } from "@blueprintjs/core";
import { IconName, IconNames } from "@blueprintjs/icons";
import { Select } from "@blueprintjs/select";
import { IFieldProps } from "decorator-form";
import React from "react";

import ArrayItemToolbar from "common/form/common/ArrayItemToolbar";
import { registerField } from "common/form/register";
import Class from "core/util/Class";
import ServiceRegistry from "core/util/ServiceRegistry";

type ValueMultiplicity = "one" | "many";

interface IEntitySelectOptions<T, V, M extends ValueMultiplicity> {
	entity: Class<T>;
	type: M;

	// get the initial value when the component mounts.
	// default (many) = { params: {ids: [...value]} }
	// default (one) = { params: { id: value } }
	getDefaultValueParams?: (value: M extends "one" ? V : V[]) => object;

	// optional data transformation when loading
	onAfterLoad?: (data: M extends "one" ? V : V[]) => M extends "one" ? V : V[];

	getSearchParams?: object | ((query: string) => object);

	// default = o.id
	getValue?: string | ((o: T) => V);

	// default = o.name
	getText?: string | ((o: T) => string);

	// default = o.id
	getKey?: string | ((o: T) => string | number);

	// optionally show extra stuff on the right side of each menu item
	getRightElement?: string | ((o: T) => string | JSX.Element);

	// optionally disable some options
	isSelectable?: (o: T) => boolean;

	// default v1 === v2
	equals?: (v1: V, v2: V) => boolean;

	// enable sort controls for the "many" type.
	// initial value will automatically sorted by comparing the results to this.props.value using the "equals" function.
	sortable?: boolean;

	onBeforeRenderItem?: (item: T, items: T[]) => JSX.Element | null;

	queryOnStartup?: string;

}

interface IEntitySelectProps<T, V, M extends ValueMultiplicity> extends IFieldProps<M extends "one" ? V : V[]> {
	options?: IEntitySelectOptions<T, V, M>;
}

interface IEntitySelectState<T> {
	data: T[];
	errors: any[];
	expanded: boolean;
	loading: boolean;
	loadingData: boolean;
	selected: T[];
	query: string;
}

export default class EntitySelect<T, V = any, M extends ValueMultiplicity = "many"> extends React.Component<IEntitySelectProps<T, V, M>, IEntitySelectState<T>> {

	public static withOptions<T, V, M extends ValueMultiplicity>(options: IEntitySelectOptions<T, V, M>) {
		return (props: IFieldProps<M extends "one" ? V : V[]>) => <EntitySelect {...props} options={options} />;
	}

	public static registered<T, V, M extends ValueMultiplicity>(options: IEntitySelectOptions<T, V, M>, valueType: string): string {
		return registerField(this.withOptions(options), options.type === 'one' ? valueType : 'array', options.type === 'many' && valueType || undefined);
	}

	public state: IEntitySelectState<T> = {
		data: [],
		errors: [],
		expanded: false,
		loading: false,
		loadingData: false,
		selected: [],
		query: ""
	};

	private timer: any;

	public render() {
		const { label, options: { type = "many" }, formData, schema = {} } = this.props;

		const title = label || schema.title;
		const description = schema.description;

		const selectProps = {
			items: this.state.data,
			itemRenderer: this.renderItem,
			inputProps: {
				leftIcon: ("search" as any)
			},
			noResults: (
				<MenuItem
					disabled={true}
					className={this.state.loadingData ? Classes.SKELETON : undefined}
					text={!!this.state.query ? "No Matches Found" : "Type Something"}
				/>
			),
			onItemSelect: this.onSelectItem,
			onQueryChange: this.onChangeQuery,
			query: this.state.query
		};

		if (type === 'one') {

			return (
				<FormGroup label={title} helperText={description}>
					<ButtonGroup>
						<Select {...selectProps}>
							<Button
								className={this.state.loading ? Classes.SKELETON : undefined}
								text={this.state.selected.length > 0 && this.getText(this.state.selected[0]) || "Nothing Selected"}
							/>
						</Select>
						{
							!!formData && (
								<Tooltip content="Remove">
									<Button
										icon={IconNames.SMALL_CROSS}
										onClick={this.onClear}
									/>
								</Tooltip>
							)
						}
					</ButtonGroup>
				</FormGroup>
			);
		}

		return (
			<FormGroup label={title} helperText={description}>
				<FormGroup>
					<ControlGroup>
						<Select {...selectProps}>
							<Button
								className={this.state.loading ? Classes.SKELETON : undefined}
								icon={IconNames.SMALL_PLUS}
								text="Select Options"
							/>
						</Select>
					</ControlGroup>
				</FormGroup>
				<FormGroup label="Selected">
					{
						this.state.selected.length > 0 ? this.state.selected.map(this.renderSelected) : (
							<p className={Classes.TEXT_MUTED}>Nothing Selected</p>
						)
					}
				</FormGroup>
			</FormGroup>
		);
	}

	public componentDidMount() {
		const { formData, options = {} as IEntitySelectOptions<T, V, M> } = this.props;

		if (options.type === 'one' && formData !== undefined && formData !== null) {
			this.setState({errors: [], loading: true}, () => {

				ServiceRegistry
					.getQueryService()
					.findOne(options.entity, typeof options.getDefaultValueParams === 'function' ? options.getDefaultValueParams(formData) : { id: formData })
					.then((result: any) => {

						this.setState({
							loading: false,
							selected: [result.data.entity]
						});

					})
					.catch((reason) => {
						this.setState({
							errors: ["Failed to load selected value", reason],
							loading: false
						});
					});
			});
			
		}

		if (options.type === 'many' && !!formData) {
			const cast = formData as V[];

			if (cast.length > 0) {

				this.setState({errors: [], loading: true}, () => {

					ServiceRegistry
						.getQueryService()
						.findMany(options.entity, typeof options.getDefaultValueParams === 'function' ? options.getDefaultValueParams(cast as any) : { ids: formData })
						.then((result: any) => {

							let selected: T[] = result.data && result.data.entities || [];

							if (selected.length > 0 && options.sortable !== false) {
								selected = cast.reduce((lst, val) => {

									const o = selected.find((s) => this.valueEquals(this.getValue(s), val));

									if (!!o) {
										lst.push(o);
									}

									return lst;
								}, []);
							}

							this.setState({loading: false, selected});
						})
						.catch((reason) => {
							this.setState({
								errors: ["Failed to load selected values", reason],
								loading: false
							});
						});
				});
			}
		}

		if(options.queryOnStartup != undefined)
			this.loadData(options.queryOnStartup);
	}

	private renderItem = (item: T, { modifiers, handleClick }) => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;

		const isSelected = this.isSelected(item);

		return (
			<React.Fragment>
			{options.onBeforeRenderItem && options.onBeforeRenderItem(item,this.state.data)}
			<MenuItem
				key={this.getKey(item)}
				text={this.getText(item)}
				icon={isSelected ? IconNames.SMALL_TICK : undefined}
				labelElement={this.getRightElement(item)}
				active={modifiers.active}
				onClick={handleClick}
				shouldDismissPopover={false}
				disabled={typeof options.isSelectable === 'function' && !options.isSelectable(item) || false}
			/>
			</React.Fragment>
		);
	}

	private renderSelected = (o: T, i: number, arr: T[]) => {

		const { options = {} as any } = this.props;

		return (
			<div key={this.getKey(o)} style={{display: 'flex', justifyContent: 'space-between', padding: '5px 0'}}>
				<div>{this.getText(o)}</div>
				<ArrayItemToolbar arr={arr} itemIndex={i} onChange={this.onChangeItem} sort={options.sortable} />
			</div>
		);
	}

	private onSelectItem = (item: T) => {
		const { formData, options = {} as IEntitySelectOptions<T, V, M>, onChange } = this.props;

		const isSelected = this.isSelected(item);

		if (options.type === 'one') {

			if (isSelected) {
				this.setState({selected: []}, () => onChange(null));
			} else {
				this.setState({selected: [item]}, () => {
					onChange(this.getValue(item) as any);
				});
			}

		} else {

			const arr = Array.isArray(formData) ? formData : (!!formData ? [formData] : []);

			const v = this.getValue(item);

			if (isSelected) {
				this.setState({
					selected: (this.state.selected || []).filter((o) => {
						return !this.valueEquals(v, this.getValue(o));
					})
				}, () => {
					onChange(arr.filter((v1) => {
						return !this.valueEquals(v, v1);
					}) as any);
				});
			} else {
				this.setState({
					selected: [...this.state.selected || [], item]
				}, () => {
					onChange([...arr, v] as any);
				});
			}
		}
	}

	private onChangeItem = (arr: T[]) => {
		this.setState({selected: arr}, () => {
			this.props.onChange(arr.map(this.getValue) as any);
		});
	}

	private onClear = () => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;

		this.setState({selected: []}, () => {
			this.props.onChange((options.type === 'one' ? null : []) as any);
		});
	}

	private onChangeQuery = (query: string) => {
		this.setState({query}, () => {
			if (!!this.timer) {
				clearTimeout(this.timer);
			}
			this.timer = setTimeout(() => this.loadData(query), 500);
		});
	}

	private loadData = (query: string) => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;

		this.setState({loadingData: true}, () => {
			ServiceRegistry
			.getQueryService()
			.findMany(options.entity, this.getSearchParams(query))
			.then((result: any) => {
				this.setState({data: result.data && result.data.entities || [], errors: [], loadingData: false});
			})
			.catch((reason) => {
				this.setState({errors: ["Failed to load options"], loading: false});
			});
		});
	}

	private getSearchParams = (query: string): object => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;

		switch(typeof options.getSearchParams) {
			case 'function':
				return (options.getSearchParams as any)(query);
			case 'object':
				return { ...options.getSearchParams, query: query || null };
			default:
				return { query: query || null };
		}
	}

	private getText = (item: T): string => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;
		return typeof options.getText === 'function' ? options.getText(item) : item && item[options.getText || 'name'];
	}

	private getValue = (item: T): V => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;
		return typeof options.getValue === 'function' ? options.getValue(item) : item && item[options.getValue || 'id'];
	}

	private getKey = (item: T): string | number => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;
		return typeof options.getKey === 'function' ? options.getKey(item) : item && item[options.getKey || 'id'];
	}

	private getRightElement = (item: T): string | JSX.Element => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;
		if (typeof options.getRightElement === 'function') {
			return options.getRightElement(item);
		}
		return !!options.getRightElement && item[options.getRightElement] || null;
	}

	private isSelected = (item: T) => {
		if (!this.state.selected || this.state.selected.length < 1) {
			return false;
		}
		const v = this.getValue(item);
		for (const s of this.state.selected) {
			if (this.valueEquals(v, this.getValue(s))) {
				return true;
			}
		}
		return false;
	}

	private valueEquals = (v1: V, v2: V) => {
		const { options = {} as IEntitySelectOptions<T, V, M> } = this.props;
		return typeof options.equals === 'function' ? options.equals(v1, v2) : v1 === v2;
	}
}



// WEBPACK FOOTER //
// ./src/common/form/fields/entitySelect/EntitySelect.tsx